"use client";

import { Tabs } from "radix-ui";
import { useState, type ReactNode } from "react";
import { useProjectText } from "@/features/project/useProjectText";
import { LayersTab, useHasLayers } from "./LayersTab";
import { ToolsTab, useHasTools } from "./ToolsTab";

/**
 * The sidebar's tabs: the results, and whatever else the tenant has enabled.
 *
 * The tab strip is hidden when there is only one tab, which is the common case —
 * a project with no map tools shows its results exactly as before, with no chrome
 * announcing a choice that doesn't exist.
 *
 * Labels go through `useProjectText` rather than `useTranslations` so a tenant can
 * rename them from their CMS; the app catalog supplies the default.
 */
export function SidebarTabs({ children }: { children?: ReactNode }) {
  const t = useProjectText();
  const [active, setActive] = useState("items");
  const hasLayers = useHasLayers();
  const hasTools = useHasTools();

  const tabs = [
    {
      value: "items",
      label: t("tabs.info"),
      icon: "nucleo_file-text1",
      content: children,
    },
    ...(hasLayers
      ? [
          {
            value: "data",
            label: t("tabs.data"),
            icon: "material_ic_format_list_bulleted_18px",
            content: <LayersTab />,
          },
        ]
      : []),
    ...(hasTools
      ? [
          {
            value: "tools",
            label: t("tabs.tools"),
            icon: "ios_Settings1",
            content: <ToolsTab />,
          },
        ]
      : []),
  ];

  return (
    <Tabs.Root
      value={active}
      onValueChange={setActive}
      className="flex min-h-0 flex-grow flex-col"
    >
      {tabs.length > 1 && (
        <Tabs.List className="tabs grid shrink-0 grid-cols-3 gap-1 bg-primary pl-5 pr-4 pt-2">
          {tabs.map((tab) => (
            <Tabs.Trigger
              key={tab.value}
              value={tab.value}
              className="flex w-full items-center justify-center gap-2 rounded-t-lg border-0 bg-gray-50 px-2 py-3 text-base leading-none shadow-[inset_0_-10px_10px_-10px_rgba(0,0,0,0.1)] outline-none hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-ring data-[state=active]:bg-white data-[state=active]:shadow-none"
            >
              <i className={`icon text-xs ic-${tab.icon}`} />
              {tab.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      )}

      {tabs.map((tab) => (
        // Not `asChild`: Radix would clone the child *component* and hand it the
        // panel's role, id and hidden state as props it never forwards to a DOM
        // node — which is how the legacy version ended up with tab panels that
        // were neither labelled nor hidden.
        //
        // The display is applied *only* to the active panel. Radix hides the
        // others with the `hidden` attribute, which is a user-agent
        // `display: none` — so any unconditional display utility here outranks it
        // and the empty panel keeps its `flex-grow` share of the sidebar.
        <Tabs.Content
          key={tab.value}
          value={tab.value}
          className="hidden min-h-0 flex-grow flex-col data-[state=active]:flex"
        >
          {tab.content}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  );
}
